import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { AppLink } from '../components/Nav';
import { DeleteAccount } from '../components/DeleteAccount';
import { ProfilePicture } from '../components/ProfilePicture';
import { Banner, Button, ErrorText, Input, PageHeader, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { shortDate } from '../lib/format';
import { fetchMe, updateMe } from '../queries/account';

/**
 * Settings: deliberately short. Your account comes from the identity provider
 * and your teams come from the API; there is nothing else to configure yet, and
 * inventing switches that do nothing would be worse than an honest gap.
 */

function providerLabel(providerId: string | undefined): string {
  switch (providerId) {
    case 'google.com':
      return 'Google';
    case 'password':
      return 'Email and password';
    case undefined:
      return 'Not recorded';
    default:
      return providerId;
  }
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-hairline py-3 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-44 shrink-0 text-caption text-chalk-faint">{label}</dt>
      <dd className="min-w-0 break-words text-body text-chalk">{children}</dd>
    </div>
  );
}

/** The tabs. Each is its own route so the address means something. */
export function SettingsTabs({
  active,
}: {
  active: 'account' | 'subscription' | 'configuration' | 'branding' | 'domain';
}) {
  const tabs = [
    { key: 'account', label: 'Account', to: '/settings' },
    { key: 'subscription', label: 'Subscription', to: '/settings/subscription' },
    // Before branding: how recordings behave is answered more often than how
    // they look, and everyone has it, team or not.
    { key: 'configuration', label: 'Configuration', to: '/settings/configuration' },
    // Shown to everyone, team or not: the page itself explains that branding
    // belongs to a team, which is a better answer than a tab that is missing
    // for reasons nobody can see.
    { key: 'branding', label: 'Branding', to: '/settings/branding' },
    // Its own tab: the rest of branding is chosen in a sitting, this is a walk
    // with waiting in it, usually done by whoever looks after DNS.
    { key: 'domain', label: 'Domain', to: '/settings/domain' },
  ] as const;
  return (
    <nav className="mb-6 flex gap-6 border-b border-hairline" aria-label="Settings sections">
      {tabs.map((tab) => (
        <AppLink
          key={tab.key}
          to={tab.to}
          aria-current={tab.key === active ? 'page' : undefined}
          className={cx(
            '-mb-px border-b-2 pb-3 text-body transition-colors duration-120',
            tab.key === active ? 'border-chalk font-medium text-chalk' : 'border-transparent text-chalk-dim hover:text-chalk',
          )}
        >
          {tab.label}
        </AppLink>
      ))}
    </nav>
  );
}

export function SettingsPage() {
  const { user, signOut } = useAuth();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled: user !== null });
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  useEffect(() => {
    track('Viewed Settings', { tab: 'account' });
  }, []);
  useEffect(() => {
    if (me.data) setName(me.data.name ?? '');
  }, [me.data]);
  const dirty = (me.data?.name ?? '') !== name.trim();
  const rename = useMutation({
    mutationFn: () => updateMe({ name: name.trim() || null }),
    onSuccess: (next) => {
      track('Profile Updated', { field: 'name' });
      queryClient.setQueryData(['me'], next);
    },
  });

  const leave = useMutation({ mutationFn: () => signOut() });

  if (!user) {
    return (
      <>
        <PageHeader title="Settings" />
        <Banner tone="info" title="You are signed out">
          Sign in again to see your account details.
        </Banner>
      </>
    );
  }

  const created = user.metadata.creationTime;

  return (
    <>
      <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
      <SettingsTabs active="account" />

      <div className="space-y-6">
        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Account</h2>
          <p className="mt-1 text-caption text-chalk-dim">
            Your name and picture are yours to change. Email and password belong to the provider.
          </p>
          {me.data ? (
            <div className="mt-4">
              <ProfilePicture me={me.data} />
            </div>
          ) : null}
          <form
            className="mt-4 max-w-xs space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate();
            }}
          >
            <Input label="Name" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
            <div className="flex items-center gap-3">
              <Button type="submit" variant="primary" size="sm" busy={rename.isPending} disabled={!dirty}>
                Save
              </Button>
              {rename.isSuccess && !dirty ? <span className="text-caption text-go">Saved</span> : null}
            </div>
            {rename.isError ? <ErrorText>{errorMessage(rename.error)}</ErrorText> : null}
          </form>
          <dl className="mt-3">
            <Row label="Email">{user.email ?? 'No email on this account'}</Row>
            <Row label="Signs in with">
              {providerLabel(user.providerData[0]?.providerId)}
            </Row>
            <Row label="Account created">
              {created ? <span className="tnum">{shortDate(created)}</span> : 'Not recorded'}
            </Row>
          </dl>
        </section>

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Sign out</h2>
          <p className="mt-1 text-body text-chalk-dim">
            Ends the session in this browser. Your recordings and share links are unaffected.
          </p>
          <Button
            variant="danger"
            className="mt-4"
            busy={leave.isPending}
            onClick={() => leave.mutate()}
          >
            Sign out
          </Button>
          {leave.isError ? (
            <div className="mt-2">
              <ErrorText>{errorMessage(leave.error)}</ErrorText>
            </div>
          ) : null}
        </section>

        <DeleteAccount />
      </div>
    </>
  );
}
